// ============================================================
// Summit League v2 — App.tsx
//
// Subdomain-aware routing:
//   summitleague.gg / localhost       → HomePage + Auth routes
//   hi.summitleague.gg / hi.localhost → DashboardPage + game routes
//
// PageLayout (Navbar + content) wraps all routes except AuthPage.
// Admin routes accessible from both bare domain and game subdomains.
//
// Analytics: initAnalytics() checks for existing consent cookie on
// mount and conditionally loads GA. CookieConsent banner handles
// first-time consent collection.
// ============================================================

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { GameProvider } from './context/GameContext';
import { useGame } from './hooks/useGame';
import { initAnalytics } from './utils/analytics';

// Layout
import PageLayout from './components/layout/PageLayout';
import ScrollToTop from './components/layout/ScrollToTop';
import CookieConsent from './components/common/CookieConsent';
import TosGate from './components/common/TosGate';

// Pages
import DashboardPage from './pages/DashboardPage';
import HomePage from './pages/HomePage';
import NewsDetailPage from './pages/NewsDetailPage';
import PublicNewsListPage from './pages/NewsListPage';
import AuthPage from './pages/AuthPage';
import UserProfile from './pages/UserProfile';
import TeamProfilePage from './pages/TeamProfilePage';
import LeaderboardPage from './pages/LeaderboardPage';
import MatchBrowsePage from './pages/MatchBrowsePage';
import CreateMatchPage from './pages/CreateMatchPage';
import CreateTeamPage from './pages/CreateTeamPage';
import MatchDashboardPage from './pages/MatchDashboardPage';
import GameRulesRouter from './pages/GameRules/GameRulesRouter';
import NotificationsPage from './pages/NotificationsPage';

// Admin
import AdminGuard from './components/admin/AdminGuard';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import DisputeListPage from './pages/admin/DisputeListPage';
import DisputeDetailPage from './pages/admin/DisputeDetailPage';
import UserListPage from './pages/admin/UserListPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import NewsListPage from './pages/admin/NewsListPage';
import NewsEditorPage from './pages/admin/NewsEditorPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import ReportListPage from './pages/admin/ReportListPage';
import ReportDetailPage from './pages/admin/ReportDetailPage';

// Legal
import LegalPage from './pages/LegalPage';

// Uncomment these as they're built:
// import MyTeamPage from './pages/MyTeamPage';

// — Subdomain Detection ————————————————————————

function isGameSubdomain(): boolean {
  const host = window.location.hostname.toLowerCase();

  // Production: {code}.summitleague.gg
  const prodMatch = host.match(/^([a-z0-9]+)\.summitleague\.gg$/);
  if (prodMatch && prodMatch[1] !== 'www') return true;

  // Local dev: {code}.localhost
  const localMatch = host.match(/^([a-z0-9]+)\.localhost(:\d+)?$/);
  if (localMatch) return true;

  // Dev fallback: ?game_id= query param
  const params = new URLSearchParams(window.location.search);
  if (params.has('game_id')) return true;

  return false;
}

// — Admin Routes (shared between both route sets) ——————————

const AdminRoutes = () => (
  <Route
    path="/admin"
    element={
      <AdminGuard requiredRole="moderator">
        <AdminLayout />
      </AdminGuard>
    }
  >
    <Route index element={<AdminDashboardPage />} />
    <Route path="disputes" element={<DisputeListPage />} />
    <Route path="disputes/:disputeId" element={<DisputeDetailPage />} />
    <Route path="users" element={<UserListPage />} />
    <Route path="users/:userId" element={<UserDetailPage />} />
    <Route path="news" element={<NewsListPage />} />
    <Route path="news/:postId" element={<NewsEditorPage />} />
    <Route path="audit-log" element={<AuditLogPage />} />
    <Route path="reports" element={<ReportListPage />} />
    <Route path="reports/:reportId" element={<ReportDetailPage />} />
  </Route>
);

// — Game Routes (subdomain) ———————————————————————

const GameRoutes: React.FC = () => {
  const { hasGame } = useGame();

  if (!hasGame) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      {/* Auth page — fullscreen, no navbar */}
      <Route path="/auth" element={<AuthPage />} />

      {/* Legal page accessible from game subdomains too */}
      <Route path="/legal" element={<PageLayout><LegalPage /></PageLayout>} />

      {/* Admin Panel */}
      {AdminRoutes()}

      {/* All other game routes get the navbar via PageLayout */}
      <Route path="/" element={<PageLayout><DashboardPage /></PageLayout>} />
      <Route path="/leaderboard" element={<PageLayout><LeaderboardPage /></PageLayout>} />
      <Route path="/matches" element={<PageLayout><MatchBrowsePage /></PageLayout>} />
      <Route path="/matches/create" element={<PageLayout><CreateMatchPage /></PageLayout>} />
      <Route path="/matches/:matchId" element={<PageLayout><MatchDashboardPage /></PageLayout>} />
      <Route path="/create-team" element={<PageLayout><CreateTeamPage /></PageLayout>} />
      <Route path="/profile/:userId" element={<PageLayout><UserProfile /></PageLayout>} />
      <Route path="/teams/:teamId" element={<PageLayout><TeamProfilePage /></PageLayout>} />
      <Route path="/rules" element={<PageLayout><GameRulesRouter /></PageLayout>} />
      <Route path="/notifications" element={<PageLayout><NotificationsPage /></PageLayout>} />
      {/*
        Future game routes — uncomment as pages are built:
        <Route path="/team" element={<PageLayout><MyTeamPage /></PageLayout>} />
      */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// — Main Routes (bare domain) ————————————————————————

const MainRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Auth page — fullscreen, no navbar */}
      <Route path="/auth" element={<AuthPage />} />

      {/* Legal page — bare domain */}
      <Route path="/legal" element={<PageLayout><LegalPage /></PageLayout>} />

      {/* Admin Panel */}
      {AdminRoutes()}

      {/* HomePage has its own layout (hero + season card + slider), gets navbar */}
      <Route path="/" element={<PageLayout><HomePage /></PageLayout>} />
      <Route path="/news" element={<PublicNewsListPage />} />
      <Route path="/news/:slug" element={<NewsDetailPage />} />
      <Route path="/profile/:userId" element={<PageLayout><UserProfile /></PageLayout>} />
      <Route path="/notifications" element={<PageLayout><NotificationsPage /></PageLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// — App ———————————————————————————————————————————

const App: React.FC = () => {
  const onGameSubdomain = isGameSubdomain();

  // Initialize analytics on mount — loads GA only if consent was previously granted
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <AuthProvider>
      <GameProvider>
        <BrowserRouter>
          <ScrollToTop />
          {onGameSubdomain ? <GameRoutes /> : <MainRoutes />}
          <TosGate />
          <CookieConsent />
        </BrowserRouter>
      </GameProvider>
    </AuthProvider>
  );
};

export default App;